import { useState } from "react";
import { platform } from "../platform";
import type { AuthError } from "../platform/PlatformAdapter";

interface ResetPasswordPageProps {
  token: string;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "0.5rem 0.75rem",
  marginBottom: "0.75rem",
  background: "rgba(0, 0, 0, 0.5)",
  border: "1px solid rgba(255, 255, 255, 0.15)",
  borderRadius: "6px",
  color: "#eee",
  fontSize: "0.95rem",
  boxSizing: "border-box",
  fontFamily: "var(--theme-font-family, inherit)",
};

function goToSignIn() {
  const url = new URL(window.location.href);
  url.pathname = "/";
  url.search = "";
  window.location.href = url.toString();
}

export function ResetPasswordPage({ token }: ResetPasswordPageProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<AuthError | null>(null);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError({ message: "Passwords don't match." });
      return;
    }
    setPending(true);
    setError(null);
    const result = await platform.resetPassword(token, password);
    setPending(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setDone(true);
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(0, 0, 0, 0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div className="theme-panel" style={{ width: "min(90%, 380px)", padding: "2rem" }}>
        {done ? (
          <>
            <h2 style={{ margin: "0 0 1rem", fontSize: "1.3rem" }}>Password updated</h2>
            <p style={{ opacity: 0.8, fontSize: "0.9rem", lineHeight: 1.5 }}>
              Your password has been reset. You can now log in with your new password.
            </p>
            <button
              onClick={goToSignIn}
              className="theme-button"
              style={{ marginTop: "1rem", padding: "0.5rem 1.25rem" }}
            >
              Go to Sign In
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2 style={{ margin: "0 0 1.5rem", fontSize: "1.3rem" }}>Choose a new password</h2>

            <input
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={inputStyle}
              required
              minLength={8}
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
              required
              minLength={8}
            />

            {error && (
              <div
                style={{
                  background: "rgba(220, 50, 50, 0.15)",
                  border: "1px solid rgba(220, 50, 50, 0.3)",
                  borderRadius: "8px",
                  padding: "0.6rem 0.8rem",
                  marginBottom: "0.75rem",
                  color: "#ff7f7f",
                  fontSize: "0.8rem",
                }}
              >
                {error.message}
                {error.supportLink && (
                  <>
                    {" "}
                    <a href={error.supportLink} style={{ color: "#ff7f7f" }}>
                      Contact support
                    </a>
                  </>
                )}
              </div>
            )}

            <button
              type="submit"
              disabled={pending}
              className="theme-button"
              style={{
                width: "100%",
                padding: "0.6rem",
                fontWeight: "bold",
                background: "var(--theme-accent, #7ec8e3)",
                color: "#111",
                opacity: pending ? 0.6 : 1,
                cursor: pending ? "not-allowed" : "pointer",
              }}
            >
              {pending ? "..." : "Reset password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
